import React, { useState } from "react";
import styles from "./EditItem.module.css";
import Select from "react-select";
import TaxonomyPicker from "../Taxonomy/TaxonomyPicker";
import { setProductTaxonomy } from "../Taxonomy/taxonomyApi";
import {
  Drawer,
  Box,
  Typography,
  Button,
  TextField,
  Divider,
  CircularProgress,
} from "@mui/material";

const EditItem = ({ editItem: initialItem, onClose, refetchAllProducts }) => {
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [selectedCategory, setSelectedCategory] = useState(1);
  const [selectedSubCategory, setSelectedSubCategory] = useState(1);
  const [openCategoryDrawer, setOpenCategoryDrawer] = useState(false);
  const [openSubCategoryDrawer, setOpenSubCategoryDrawer] = useState(false);

  // Drawer inputs
  const [newCategory, setNewCategory] = useState("");
  const [newSubCategory, setNewSubCategory] = useState("");
  const [newSubCategoryCat, setNewSubCategoryCat] = useState(null);

  React.useEffect(() => {
    let cate = [];
    async function fetchCategoryData() {
      setIsLoaded(false);
      try {
        fetch(`${process.env.REACT_APP_SERVER_URL}/get_all_categories`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "GET",
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else {
              console.log("some error");
              return response.json();
            }
          })
          .then((data) => {
            cate = data.data;
            setCategories(data.data);
          })
          .catch((error) => console.log(error));
      } catch (error) {
        console.log(error);
      }
    }
    async function fetchSubCategoryData() {
      try {
        fetch(`${process.env.REACT_APP_SERVER_URL}/get_all_subcategories`, {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
          },
          credentials: "include",
          method: "GET",
        })
          .then((response) => {
            if (response.status === 200) {
              return response.json();
            } else {
              setIsLoaded(true);
              console.log("some error");
              return response.json();
            }
          })
          .then((data) => {
            setSubCategories(data.data);
            const subcategory = data.data.find(
              (obj) => obj.id === initialItem.sub_category_id
            );
            const category = cate.find((obj) => obj.id === subcategory.id);
            setSelectedCategory(category);
            setSelectedSubCategory(subcategory);
            setIsLoaded(true);
          })
          .catch((error) => console.log(error));
      } catch (error) {
        console.log(error);
        setIsLoaded(true);
      }
    }
    fetchCategoryData();
    fetchSubCategoryData();
    // eslint-disable-next-line
  }, []);

  const categoryOptions = categories?.map((c) => ({
    value: c.id,
    label: c.name,
  }));

  const subCategoryOptions = subCategories
    ?.filter((sc) => selectedCategory && sc.category_id === selectedCategory.id)
    ?.map((sc) => ({ value: sc.id, label: sc.name }));

  const addCategory = async () => {
    const name = newCategory.trim();
    if (!name) return;
    const res = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/add_category`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: name }),
      }
    );

    if (res.ok) {
      alert("Category added successfully");
      onClose();
    } else {
      const err = await res.json();
      alert("Error: " + (err.message || "Failed to add product"));
    }
    setNewCategory("");
    setOpenCategoryDrawer(false);
  };

  const addSubCategory = async () => {
    const name = newSubCategory.trim();
    if (!name || !newSubCategoryCat) return;
    const res = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/add_subcategory`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: name,
          category_id: selectedCategory,
        }),
      }
    );

    if (res.ok) {
      alert("Category added successfully");
      onClose();
    } else {
      const err = await res.json();
      alert("Error: " + (err.message || "Failed to add product"));
    }
    setNewSubCategory("");
    setNewSubCategoryCat(null);
    setOpenSubCategoryDrawer(false);
  };

  const [editItem, setEditItem] = useState(() => {
    const fallback = {
      app_name: "",
      img_url: "",
      margin: 0,
      extra_discount: null,
      easexpense_verified: false,
      description: "",
      features: [""],
      pricing: {
        india: [],
        global: [],
      },
    };

    return {
      ...fallback,
      ...initialItem,
      features: Array.isArray(initialItem?.features)
        ? initialItem.features
        : [""],
      pricing: {
        india: initialItem?.pricing?.india || [],
        global: initialItem?.pricing?.global || [],
      },
    };
  });

  // Industry / Compliance / Certification attached to this listing. Held
  // separately from editItem because they live in their own join tables, not
  // in all_products — the nightly website-listing sync rewrites that row and
  // would drop anything stored on it. `null` until the picker has loaded.
  const [taxonomy, setTaxonomy] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditItem((prev) => ({ ...prev, [name]: value }));
  };

  const handleToggle = () => {
    setEditItem((prev) => ({
      ...prev,
      easexpense_verified: !prev.easexpense_verified,
    }));
  };

  const handlePricingChange = (region, index, field, value) => {
    const updatedPricing = { ...editItem.pricing };

    if (!updatedPricing[region]) {
      updatedPricing[region] = [];
    }

    updatedPricing[region][index] = {
      ...updatedPricing[region][index],
      [field]: value,
    };

    setEditItem((prev) => ({
      ...prev,
      pricing: updatedPricing,
    }));
  };

  const addPricingRow = (region) => {
    setEditItem((prev) => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [region]: [
          ...(prev.pricing?.[region] || []),
          { interval: "", price: "" },
        ],
      },
    }));
  };

  const handleFeatureChange = (index, value) => {
    const updated = [...editItem.features];
    updated[index] = value;
    setEditItem((prev) => ({ ...prev, features: updated }));
  };

  const addFeature = () => {
    setEditItem((prev) => ({
      ...prev,
      features: [...prev.features, ""],
    }));
  };

  const removeFeature = (index) => {
    const updated = [...editItem.features];
    updated.splice(index, 1);
    setEditItem((prev) => ({
      ...prev,
      features: updated,
    }));
  };

  const submitHandler = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/edit-product/${editItem.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify(editItem),
        }
      );

      const data = await response.json();

      // 200 only: the admin auth middleware answers 201/202 (still "ok") when
      // the session is missing or expired.
      if (response.status === 200) {
        // Tags are a separate write against their own tables. Done after the
        // product update so a tag failure cannot lose the edits the admin
        // actually typed — it reports separately and leaves the rest saved.
        if (taxonomy && editItem.id) {
          try {
            await setProductTaxonomy(editItem.id, taxonomy);
          } catch (taxErr) {
            console.error("Failed to save tags:", taxErr);
            alert(
              `Product saved, but the tags could not be updated: ${
                taxErr?.message || "unknown error"
              }`
            );
            onClose();
            refetchAllProducts();
            return;
          }
        }

        alert("Product updated successfully");
        onClose();
        refetchAllProducts();
      } else {
        console.error("Failed to update product:", data.message || data.msg);
        alert(data.msg ? `Failed to update product: ${data.msg}` : "Failed to update product");
      }
    } catch (error) {
      console.error("Error while updating:", error);
      alert("An error occurred during update");
    }
  };

  if (!editItem) return null;

  return (
    <>
      {!isLoaded && (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CircularProgress />
        </Box>
      )}
      {isLoaded && (
        <div
          className={styles.modalBackdrop}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          <div
            className={styles.modalContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className={styles.modalTitle}>Edit App</h2>

            <input
              type="text"
              name="app_name"
              value={editItem.app_name}
              onChange={handleChange}
              placeholder="App Name"
              className={styles.modalInput}
            />

            <input
              type="text"
              name="img_url"
              value={editItem.img_url || ""}
              onChange={handleChange}
              placeholder="Image URL"
              className={styles.modalInput}
            />

            <label className={styles.modalLabel}>Margin %</label>
            <input
              type="number"
              name="margin"
              value={editItem.margin || ""}
              onChange={handleChange}
              placeholder="Margin %"
              className={styles.modalInput}
            />

            <label className={styles.modalLabel}>
              Extra Discount % (limited time)
            </label>
            <input
              type="number"
              name="extra_discount"
              value={editItem.extra_discount || ""}
              onChange={handleChange}
              placeholder="Extra Discount (Limited time)%"
              className={styles.modalInput}
            />

            <label className={styles.modalLabel}>Category</label>
            <div style={{ marginBottom: "2%" }}>
              <Select
                options={categoryOptions}
                value={
                  selectedCategory
                    ? {
                        value: selectedCategory.id,
                        label: selectedCategory.name,
                      }
                    : null
                }
                onChange={(option) => {
                  const cat =
                    categories.find((c) => c.id === option.value) || null;
                  setSelectedCategory(cat);
                  setSelectedSubCategory(null); // reset subcat
                }}
                placeholder="Select Category"
              />
              <button
                style={{ marginTop: "2%" }}
                type="button"
                onClick={() => setOpenCategoryDrawer(true)}
              >
                + Add
              </button>
            </div>

            {/* SubCategory Select + Add */}
            <label className={styles.modalLabel}>Sub Category</label>
            <div style={{ marginBottom: "2%" }}>
              <Select
                options={subCategoryOptions}
                value={
                  selectedSubCategory
                    ? {
                        value: selectedSubCategory.id,
                        label: selectedSubCategory.name,
                      }
                    : null
                }
                onChange={(option) => {
                  const sub =
                    subCategories.find((s) => s.id === option.value) || null;
                  setSelectedSubCategory(sub);
                }}
                placeholder="Select Sub Category"
                className="flex-1"
                isDisabled={!selectedCategory}
                noOptionsMessage={() =>
                  selectedCategory
                    ? "No subcategories"
                    : "Select a category first"
                }
              />
              <button
                type="button"
                style={{ marginTop: "2%" }}
                onClick={() => {
                  // Default the drawer's category to the currently selected one
                  if (selectedCategory) setNewSubCategoryCat(selectedCategory);
                  setOpenSubCategoryDrawer(true);
                }}
              >
                + Add
              </button>
            </div>

            {/* Industry / Compliance / Certification.
                Stored in their own join tables keyed on all_products.id, so
                the nightly website-listing re-publish cannot wipe them. */}
            <div style={{ marginTop: "3%" }}>
              <TaxonomyPicker
                target="product"
                id={editItem.id}
                value={taxonomy}
                onChange={setTaxonomy}
                compact
              />
            </div>

            {/* Easexpense Verified Toggle */}
            <div className={styles.toggleContainer}>
              <label className={styles.modalLabel}>Easexpense Verified</label>
              <label className={styles.toggleSwitch}>
                <input
                  type="checkbox"
                  checked={editItem.easexpense_verified}
                  onChange={handleToggle}
                />
                <span className={styles.slider}></span>
              </label>
            </div>

            {/* Description Field */}
            <div className={styles.descriptionContainer}>
              <label className={styles.modalLabel}>Description</label>
              <textarea
                name="description"
                value={editItem.description}
                onChange={handleChange}
                rows={4}
                placeholder="Write a short description"
                className={styles.modalTextArea}
              />
            </div>

            {/* Features Field */}
            <div>
              <label className={styles.modalLabel}>Features</label>
              {editItem?.features?.map((feature, index) => (
                <div key={index} className={styles.featureGroup}>
                  <input
                    type="text"
                    placeholder={`Feature ${index + 1}`}
                    value={feature}
                    onChange={(e) => handleFeatureChange(index, e.target.value)}
                    className={styles.modalInput}
                  />
                  {editItem?.features?.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className={styles.removeFeatureButton}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addFeature}
                className={styles.addPricingButton}
              >
                + Add New Feature
              </button>
            </div>

            {/* Pricing India */}
            <h3 className={styles.modalSubtitle}>Pricing (India)</h3>
            {(editItem?.pricing?.india || []).map((item, index) => (
              <div key={`india-${index}`} className={styles.pricingGroup}>
                <input
                  type="text"
                  placeholder="Interval (e.g., month)"
                  value={item.interval}
                  onChange={(e) =>
                    handlePricingChange(
                      "india",
                      index,
                      "interval",
                      e.target.value
                    )
                  }
                  className={styles.modalInput}
                />
                <input
                  type="text"
                  placeholder="Price (e.g., 199.00–999.00)"
                  value={item.price}
                  onChange={(e) =>
                    handlePricingChange("india", index, "price", e.target.value)
                  }
                  className={styles.modalInput}
                />
              </div>
            ))}
            <button
              onClick={() => addPricingRow("india")}
              className={styles.addPricingButton}
            >
              + Add India Pricing
            </button>

            {/* Pricing Global */}
            <h3 className={styles.modalSubtitle}>Pricing (Global)</h3>
            {(editItem?.pricing?.global || []).map((item, index) => (
              <div key={`global-${index}`} className={styles.pricingGroup}>
                <input
                  type="text"
                  placeholder="Interval (e.g., year)"
                  value={item.interval}
                  onChange={(e) =>
                    handlePricingChange(
                      "global",
                      index,
                      "interval",
                      e.target.value
                    )
                  }
                  className={styles.modalInput}
                />
                <input
                  type="text"
                  placeholder="Price (e.g., 30.00–100.00)"
                  value={item.price}
                  onChange={(e) =>
                    handlePricingChange(
                      "global",
                      index,
                      "price",
                      e.target.value
                    )
                  }
                  className={styles.modalInput}
                />
              </div>
            ))}
            <button
              onClick={() => addPricingRow("global")}
              className={styles.addPricingButton}
            >
              + Add Global Pricing
            </button>

            <div className={styles.modalButtons}>
              <button
                className={`${styles.modalButton} ${styles.modalCancel}`}
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className={`${styles.modalButton} ${styles.modalSave}`}
                onClick={submitHandler}
              >
                Submit
              </button>
            </div>
          </div>
          <Drawer
            anchor="right"
            open={openCategoryDrawer}
            onClose={() => setOpenCategoryDrawer(false)}
            sx={{ width: "50%" }}
          >
            <Box sx={{ width: 340, p: 2 }} role="presentation">
              <Typography variant="h6" sx={{ mb: 1 }}>
                Add Category
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <TextField
                label="Category Name"
                fullWidth
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                autoFocus
              />
              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Button variant="contained" onClick={addCategory}>
                  Save
                </Button>
                <Button onClick={() => setOpenCategoryDrawer(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Drawer>

          {/* Drawer: Add Sub Category */}
          <Drawer
            anchor="right"
            open={openSubCategoryDrawer}
            onClose={() => setOpenSubCategoryDrawer(false)}
            sx={{ width: "50%" }}
          >
            <Box sx={{ width: 340, p: 2 }} role="presentation">
              <Typography variant="h6" sx={{ mb: 1 }}>
                Add Sub Category
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <TextField
                label="Sub Category Name"
                fullWidth
                value={newSubCategory}
                onChange={(e) => setNewSubCategory(e.target.value)}
              />

              <div className="mt-3">
                <Select
                  options={categoryOptions}
                  value={
                    newSubCategoryCat
                      ? {
                          value: newSubCategoryCat.id,
                          label: newSubCategoryCat.name,
                        }
                      : null
                  }
                  onChange={(option) => {
                    const cat =
                      categories.find((c) => c.id === option.value) || null;
                    setNewSubCategoryCat(cat);
                  }}
                  placeholder="Select Category"
                />
              </div>

              <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
                <Button variant="contained" onClick={addSubCategory}>
                  Save
                </Button>
                <Button onClick={() => setOpenSubCategoryDrawer(false)}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </Drawer>
        </div>
      )}
    </>
  );
};

export default EditItem;
